<template>
    <v-container
        fluid
    >
        <v-toolbar
            id="custom-toolbar"
            flat
        >
            <v-toolbar-title>
                Auto Respond Rules
            </v-toolbar-title>
        </v-toolbar>
        <v-row>
            <v-col
                cols="6"
            >
                <v-select
                    v-model="response_code"
                    :items="selectOptions"
                    item-text="label"
                    item-value="value"
                    label="Rules for All Response Codes"
                    outlined
                    dense
                    @change="getAutoResponseRules"
                />
            </v-col>
            <v-col
                cols="6"
                class="text-right"
            >
                <router-link
                    to="/settings/auto-respond-rules/new"
                    class="text-decoration-none"
                >
                    <v-btn
                        color="primary"
                        class="text-subtitle-2"
                        depressed
                    >
                        <v-icon color="white">
                            mdi-plus
                        </v-icon>
                        Add Rule
                    </v-btn>
                </router-link>
            </v-col>
        </v-row>
        <div v-if="!loading">
            <v-row
                v-for="rule in groupedRules"
                :key="rule.response_code"
                class="mb-2"
            >
                <v-col cols="12">
                    <span class="title">
                        {{ rule.response_code }}: "{{ rule.description }}"
                        <v-chip
                            color="primary"
                            outlined
                            class="ml-2"
                        >
                            <span>{{ rule.items.length }}</span>
                        </v-chip>
                    </span>
                </v-col>
                <v-col cols="12">
                    <router-link
                        to="/settings/auto-respond-rules/edit"
                        class="text-decoration-none"
                    > 
                        <v-card
                            v-for="item in rule.items"
                            :key="item.rowid"
                            class="mb-3"
                            hover
                            @click="selectAutoResponseRule(item)"
                        >
                            <v-list two-line>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-row>
                                            <v-col
                                                v-if="!item.enabled"
                                                cols="12"
                                            >
                                                <v-chip
                                                    color="red"
                                                    outlined
                                                >
                                                    Disabled
                                                </v-chip>
                                            </v-col>
                                            <v-col
                                                cols="2"
                                                class="font-weight-bold"
                                            >
                                                Name:
                                            </v-col>
                                            <v-col
                                                cols="10"
                                            >
                                                {{ item.name }}
                                            </v-col>
                                            <v-col
                                                v-if="item.weight"
                                                cols="2"
                                                class="font-weight-bold"
                                            >
                                                Weight:
                                            </v-col>
                                            <v-col
                                                v-if="item.weight"
                                                cols="10"
                                            >
                                                {{ item.weight }}
                                            </v-col>
                                            <v-col
                                                v-if="item.center"
                                                cols="2"
                                                class="font-weight-bold"
                                            >
                                                Center:
                                            </v-col>
                                            <v-col
                                                v-if="item.center"
                                                cols="10"
                                            >
                                                {{ item.center }}
                                            </v-col>
                                            <v-col
                                                v-if="item.state"
                                                cols="2"
                                                class="font-weight-bold"
                                            >
                                                State:
                                            </v-col>
                                            <v-col
                                                v-if="item.state"
                                                cols="10"
                                            >
                                                {{ item.state }}
                                            </v-col>
                                            <v-col
                                                v-if="item.work_type"
                                                cols="2"
                                                class="font-weight-bold"
                                            >
                                                Work Type:
                                            </v-col>
                                            <v-col
                                                v-if="item.work_type"
                                                cols="10"
                                            >
                                                {{ item.work_type }}
                                            </v-col>
                                        </v-row>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-list-item-icon class="ma-0">
                                            <v-btn
                                                icon
                                                large
                                            >
                                                <v-icon>
                                                    chevron_right
                                                </v-icon>
                                            </v-btn>
                                        </v-list-item-icon>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </v-card>
                    </router-link>
                </v-col>
            </v-row>
        </div>
        <div
            v-if="loading"
            class="d-flex justify-center align-center w-100p"
            style="height: 70vh;"
        >
            <v-progress-circular
                :size="50"
                color="primary"
                indeterminate
            />
        </div>
    </v-container>
</template>

<script>
import '@/utils/uniq';
import webtmsbackend from '@/services/webtmsBackend';
export default ({
    data() {
        return {
            centersPosRespCodes: this.$store.getters.centersPosRespCodes,
            groupedRules: [],
            loading: false,
            response_code: '*'
        };
    },
    computed: {
        selectOptions() {
            let result = [{label:'Rules For All Response Codes', value: '*'}];
            let companyResponseCodes = this.$store.getters.companyResponseCodes;
            companyResponseCodes.forEach(element => {
                result.push({
                    label: (element.response_code === '---' ? '' : element.response_code + ': ') + element.description,
                    value: element.response_code
                });
            });
            return result;
        },
    },
    async mounted() {
        this.$store.commit('SET_SELECTED_AUTO_RESPONSE_RULE', {});
        this.getAutoResponseRules();
    },
    methods: {
        selectAutoResponseRule(autoResponseRule) {
            this.$store.commit('SET_SELECTED_AUTO_RESPONSE_RULE', autoResponseRule);
        },
        async getAutoResponseRules() {
            this.loading = true;
            let rules = await webtmsbackend.getAutoResponseRules(this.response_code);
            this.groupedRules = await this.groupResponseCodes(rules);
            this.loading = false;
        },
        async groupResponseCodes(rules) { 
            let groups = [];
            let result = [];
            // Get all response codes into an array
            for(let rule of rules.data) {
                groups.push(rule.response_code);
            }
            // Remove duplicates
            groups = groups.uniq();
            // Group responses with the same response code
            for(let responseCode of groups) {
                let groupItem = { response_code: responseCode, description: '', items: [] };
                for(let rule of rules.data) {
                    if(rule.response_code === responseCode) {
                        groupItem.description = rule.description;
                        groupItem.items.push(rule);
                    }
                }
                result.push(groupItem);
            }
            return result;
        },
    },
});
</script>
<style>
    #custom-toolbar .v-toolbar__content {
        padding: 0px !important;
    }
</style>